import {
  downloadFile,
  retrieveFileTree,
} from "@/lib/data/requests/files/canonical-files";
import {
  adaptFile,
  canDownloadFile,
} from "@/lib/data/requests/files/file-adapter";
import { generateS3ItemPreSignedUrlV1 } from "@/lib/data/requests/s3";
import type { MatterFile } from "@/lib/types";
import { downloadZip, type ZipDownloadItem } from "@/lib/utils/downloadZip";
import { downloadFileLink, handleDownload } from "@/lib/utils/matterFile";
import type { RepoItem } from "./FileRepository";

export const canDownloadRepoItem = (item: RepoItem) =>
  Boolean(item.isFolder) ||
  (item.kind === "share-folder"
    ? Boolean(item.presignedUrlKey)
    : canDownloadFile(item));

const itemKey = (item: RepoItem) => `${item.kind ?? "regular"}:${item.id}`;

const getDownloadUrl = async (item: RepoItem) => {
  if (!canDownloadRepoItem(item) || item.isFolder) {
    throw new Error(`File ${item.name} is not available to download.`);
  }
  if (item.kind !== "share-folder") {
    return (await downloadFile(item.id)).url;
  }
  const presignedKey = item.presignedUrlKey;
  if (!presignedKey)
    throw new Error(`File ${item.name} is not available to download.`);
  const presigned = await generateS3ItemPreSignedUrlV1(
    "matter-share-folder-file",
  )({ clientMethod: "get_object", key: presignedKey });
  return presigned.data.presignedUrl;
};

const getShareFolderDescendants = (folder: RepoItem, items: RepoItem[]) => {
  const descendants: RepoItem[] = [];
  const pending = [folder.id];
  const visited = new Set<string>();
  while (pending.length) {
    const parentId = pending.pop();
    if (parentId === undefined || visited.has(parentId)) continue;
    visited.add(parentId);
    for (const item of items) {
      if (item.kind !== "share-folder" || item.idLocation !== parentId)
        continue;
      descendants.push(item);
      if (item.isFolder) pending.push(item.id);
    }
  }
  return descendants;
};

export async function downloadRepositoryItems(
  items: RepoItem[],
  repositoryItems: RepoItem[],
) {
  const selected = items.filter(canDownloadRepoItem);
  if (!selected.length) {
    throw new Error("No selected files are available to download.");
  }

  const single = selected[0];
  if (items.length === 1 && !single.isFolder) {
    if (single.kind !== "share-folder") {
      const result = await downloadFile(single.id);
      downloadFileLink(result.fileName, result.url);
    } else {
      await handleDownload(single.name, await getDownloadUrl(single), true);
    }
    return;
  }

  const childrenByParent = new Map<string, RepoItem[]>();
  const covered = new Set<string>();
  for (const folder of selected.filter((item) => item.isFolder)) {
    // Read the complete, paginated subtree rather than the currently visible files.
    const descendants: RepoItem[] =
      folder.kind === "share-folder"
        ? getShareFolderDescendants(folder, repositoryItems)
        : (await retrieveFileTree({ parentId: folder.id })).map((file) => ({
            ...adaptFile<MatterFile>(file),
            kind: "regular" as const,
          }));
    for (const item of descendants) {
      covered.add(itemKey(item));
      const parentKey = `${item.kind ?? "regular"}:${item.idLocation}`;
      const siblings = childrenByParent.get(parentKey) ?? [];
      if (!siblings.some((sibling) => itemKey(sibling) === itemKey(item))) {
        siblings.push(item);
      }
      childrenByParent.set(parentKey, siblings);
    }
  }

  const visited = new Set<string>();
  const toZipItems = (files: RepoItem[]): ZipDownloadItem[] =>
    files.flatMap((item): ZipDownloadItem[] => {
      const key = itemKey(item);
      if (visited.has(key)) return [];
      visited.add(key);
      if (item.isFolder) {
        return [
          {
            name: item.name,
            children: toZipItems(childrenByParent.get(key) ?? []),
          },
        ];
      }
      return [{ name: item.name, getUrl: () => getDownloadUrl(item) }];
    });

  await downloadZip(
    toZipItems(selected.filter((item) => !covered.has(itemKey(item)))),
    items.length === 1 && single.isFolder ? `${single.name}.zip` : "files.zip",
  );
}
